/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { OperationalFilterSearchField } from '../../shared/filters/OperationalFilterPrimitives'
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { useOperationalWebSearchFocus } from '../../shared/ui/hooks-screen/useOperationalWebSearchFocus';
import { formatMoneyCompact } from '../../shared/format/moneyFormat';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet'
import type { TicketMoreAction } from '../../shared/ticket/TicketMoreActionsSheet';
import { showInfo } from '../../shared/ui/appToast';
import type { CustomerAccountRow, CustomerDaySummary, CustomerKpiSummary } from '../domain/customerTypes';

type Props = {
  rows: CustomerAccountRow[];
  loading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  canEdit: boolean;
  onAddCustomer: () => void;
  onBulkRemind?: () => void;
  onOpenCsv?: () => void;
  onConfigurePortal?: () => void;
  onFilterOverdue?: () => void;
  overdueSelected?: boolean;
  formatMoney: (n: number) => string;
  filterTrigger?: React.ReactNode;
  daySummary?: CustomerDaySummary | null;
  daySummaryLoading?: boolean;
  kpiSummary?: CustomerKpiSummary | null;
  kpiSummaryLoading?: boolean;
};

export function CustomersScreenHeader({
  search,
  onSearchChange,
  canEdit,
  onAddCustomer,
  onBulkRemind,
  onOpenCsv,
  onConfigurePortal,
  onFilterOverdue,
  overdueSelected,
  filterTrigger,
  daySummary,
  daySummaryLoading,
  kpiSummary,
  kpiSummaryLoading,
}: Props) {
  const { layoutWide } = useOperationalFilterLayout();
  const searchInputRef = useRef<TextInput>(null);
  const [moreActionsSheetOpen, setMoreActionsSheetOpen] = useState(false);
  useOperationalWebSearchFocus(searchInputRef, {
    shortcutsEnabled: true,
  });

  const kpis = kpiSummary ?? { debtTotal: 0, debtCount: 0, blockedCount: 0, overdueCount: 0 };
  const kpisLoading = kpiSummaryLoading && !kpiSummary;
  const dayLoading = daySummaryLoading && !daySummary;

  const kpiItems = useMemo(
    (): TicketKpiItem[] => [
      {
        label: 'Deuda total',
        value: kpisLoading ? '…' : formatMoneyCompact(kpis.debtTotal),
        tone: kpis.debtTotal > 0 ? ('bad' as const) : ('default' as const),
      },
      {
        label: 'Cobrado hoy',
        value: dayLoading ? '…' : formatMoneyCompact(daySummary?.paymentsTotal ?? 0),
        tone: (daySummary?.paymentsTotal ?? 0) > 0 ? ('ok' as const) : ('default' as const),
      },
      {
        label: 'Cargado hoy',
        value: dayLoading ? '…' : formatMoneyCompact(daySummary?.chargesTotal ?? 0),
        tone: (daySummary?.chargesTotal ?? 0) > 0 ? ('warn' as const) : ('default' as const),
      },
      {
        label: 'Vencidos',
        value: kpisLoading ? '…' : String(kpis.overdueCount),
        tone: kpis.overdueCount > 0 ? ('warn' as const) : ('default' as const),
        onPress: onFilterOverdue,
        selected: overdueSelected,
      },
    ],
    [kpis.debtTotal, kpis.overdueCount, kpisLoading, daySummary?.paymentsTotal, daySummary?.chargesTotal, dayLoading]
  );

  const sheetActions = useMemo((): TicketMoreAction[] => {
    const items: TicketMoreAction[] = [
      {
        key: 'remind',
        label: `Recordar vencidos${kpis.overdueCount > 0 ? ` (${kpis.overdueCount})` : ''}`,
        hint: 'Mandá WhatsApp a los que vencieron',
        icon: 'notifications-outline',
        onPress: onBulkRemind ?? (() => {}),
        disabled: !onBulkRemind || kpis.overdueCount === 0,
      },
    ];
    if (onOpenCsv) {
      items.push({
        key: 'csv',
        label: 'Importar / Exportar CSV',
        hint: 'Cargar, actualizar o llevarse el listado',
        icon: 'swap-vertical-outline',
        onPress: onOpenCsv,
        disabled: false,
        testID: 'customers.csv',
      });
    }
    if (onConfigurePortal) {
      items.push({
        key: 'portal',
        label: 'Link de pago',
        hint: 'Todavía no está. El cliente va a pagar con un link.',
        icon: 'globe-outline',
        onPress: onConfigurePortal,
        disabled: true,
      });
    }
    return items;
  }, [onBulkRemind, kpis.overdueCount, onOpenCsv, onConfigurePortal]);

  const moreActionsSheet = (
    <TicketMoreActionsSheet
      visible={moreActionsSheetOpen}
      title="Acciones de clientes"
      actions={sheetActions}
      onClose={() => setMoreActionsSheetOpen(false)}
    />
  );

  const explainReadonlyAdd = () =>
    showInfo('Solo lectura', 'Necesitás permiso de administrador para agregar clientes.');

  const primaryButton = (
    <TouchableOpacity
      style={[styles.deskBtnGold, !canEdit && posUi.disabledCard]}
      onPress={canEdit ? onAddCustomer : explainReadonlyAdd}
      accessibilityRole="button"
      accessibilityLabel="Agregar cliente"
      accessibilityHint={canEdit ? undefined : 'Necesitás permiso de administrador'}
      accessibilityState={{ disabled: !canEdit }}
      testID="customers.add-customer"
    >
      <Ionicons name="add" size={17} color={ticketColors.goldInk} />
      <Text style={styles.deskBtnGoldText}>Agregar cliente</Text>
    </TouchableOpacity>
  );

  // ── Wide: TicketModuleHeader con search + filtros + Más en tools ──
  if (layoutWide) {
    return (
      <View style={styles.headerStack}>
        <TicketModuleHeader
          kicker="Clientes"
          title="Clientes del local"
          meta="Saldos, límites y cobros"
          testID="customers.header.sheet"
          trailing={primaryButton}
          tools={
            <View style={styles.deskToolsRow}>
              <View style={styles.deskSearchFlex}>
                <OperationalFilterSearchField
                  inputRef={searchInputRef}
                  value={search}
                  onChangeText={onSearchChange}
                  placeholder="Buscar por nombre, teléfono o CUIT"
                  appearance="desk"
                  dense
                  boxStyle={styles.deskSearchBox}
                  testID="customers.search"
                  accessibilityLabel="Buscar cliente"
                />
              </View>
              <View style={styles.deskToolsSpacer} />
              {filterTrigger}
              {onBulkRemind ? (
                <TouchableOpacity
                  style={[styles.deskBtn, kpis.overdueCount === 0 && posUi.disabledCard]}
                  onPress={onBulkRemind}
                  disabled={kpis.overdueCount === 0}
                  accessibilityRole="button"
                  accessibilityLabel={`Recordar a ${kpis.overdueCount} cliente${kpis.overdueCount !== 1 ? 's' : ''} vencidos`}
                  accessibilityState={{ disabled: kpis.overdueCount === 0 }}
                  testID="customers.remind-overdue"
                >
                  <Text style={styles.deskBtnText}>
                    Recordar vencidos{kpis.overdueCount > 0 ? ` (${kpis.overdueCount})` : ''}
                  </Text>
                </TouchableOpacity>
              ) : null}
              {sheetActions.length > 0 ? (
                <TouchableOpacity
                  style={styles.deskBtn}
                  onPress={() => setMoreActionsSheetOpen(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Más acciones de clientes"
                  testID="customers.more.open"
                >
                  <View style={styles.deskBtnInner}>
                    <Text style={styles.deskBtnText}>Más</Text>
                    <Ionicons name="chevron-down" size={14} color={ticketColors.ink} />
                  </View>
                </TouchableOpacity>
              ) : null}
            </View>
          }
        >
          {moreActionsSheet}
        </TicketModuleHeader>

        <PaperSheet testID="customers.kpi.sheet">
          <TicketKpiStrip items={kpiItems} layout="desk" variant="soft" />
        </PaperSheet>
      </View>
    );
  }

  // ── Mobile: TicketModuleHeader con search + botones en tools ──
  return (
    <View style={styles.headerStack}>
      <TicketModuleHeader
        kicker="Clientes"
        title="Clientes del local"
        meta="Saldos, límites y cobros"
        testID="customers.header.sheet"
        tools={
          <>
            <View style={styles.searchRowMobile}>
              <View style={styles.searchBoxMobile}>
                <Ionicons name="search-outline" size={18} color={ticketColors.ink3} />
                <TextInput
                  ref={searchInputRef}
                  value={search}
                  onChangeText={onSearchChange}
                  placeholder="Buscar por nombre, teléfono o CUIT"
                  placeholderTextColor={ticketColors.ink3}
                  style={styles.searchInputMobile}
                  accessibilityLabel="Buscar cliente"
                  autoCorrect={false}
                  returnKeyType="search"
                  testID="customers.search"
                />
                {search.trim().length > 0 ? (
                  <TouchableOpacity
                    onPress={() => onSearchChange('')}
                    accessibilityRole="button"
                    accessibilityLabel="Limpiar búsqueda"
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                  >
                    <Ionicons name="close-circle" size={20} color={ticketColors.ink2} />
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>

            <View style={styles.actionsRowMobile}>
              <TouchableOpacity
                style={[styles.actionPrimaryMobile, !canEdit && posUi.disabledCard]}
                onPress={canEdit ? onAddCustomer : explainReadonlyAdd}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Agregar cliente"
                accessibilityHint={canEdit ? undefined : 'Necesitás permiso de administrador'}
                accessibilityState={{ disabled: !canEdit }}
                testID="customers.add-customer"
              >
                <Text style={styles.actionPrimaryTextMobile}>Agregar cliente</Text>
              </TouchableOpacity>
              {sheetActions.length > 0 ? (
                <TouchableOpacity
                  style={styles.actionMoreMobile}
                  onPress={() => setMoreActionsSheetOpen(true)}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Más acciones de clientes"
                  testID="customers.more.open"
                >
                  <Ionicons name="ellipsis-horizontal" size={20} color={ticketColors.ink} />
                </TouchableOpacity>
              ) : null}
            </View>

            {filterTrigger}
          </>
        }
      >
        {moreActionsSheet}
      </TicketModuleHeader>

      <PaperSheet testID="customers.kpi.sheet">
        <TicketKpiStrip items={kpiItems} layout="mobile" variant="soft" />
      </PaperSheet>
    </View>
  );
}

const styles = StyleSheet.create({
  headerStack: {
    gap: 10,
    paddingBottom: spacing.sm,
  },
  deskToolsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  deskSearchFlex: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 280,
    minWidth: 180,
    // Proto: la caja no se estira infinito en paneles muy anchos.
    maxWidth: 560,
  },
  // Empuja el grupo de acciones (Filtros / Recordar / Más) al borde derecho.
  deskToolsSpacer: {
    flexGrow: 1,
    minWidth: 8,
  },
  // Proto: search en tile + botones blancos con borde de control (más marcado).
  deskSearchBox: {
    backgroundColor: ticketColors.tile,
    borderColor: ticketColors.rule,
  },
  deskBtn: {
    height: 44,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deskBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  deskBtnInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  deskBtnGold: {
    height: 44,
    paddingHorizontal: 18,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  deskBtnGoldText: {
    fontSize: 13.5,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  searchRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchBoxMobile: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    paddingHorizontal: 12,
    height: 42,
  },
  searchInputMobile: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    paddingVertical: 0,
  },
  actionsRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionPrimaryMobile: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionPrimaryTextMobile: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  actionMoreMobile: {
    width: 44,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
